// Sliding puzzle interface: tap a tile in line with the gap and it slides over (with the tiles between it and the
// gap); the arrow keys slide the tile on that side of the gap; unlimited undo, a hint, no time limit. The picture
// is one of the jigsaw paintings cropped square, or plain numbers; a small number sits on every picture tile.
// The grid is in the server HTML in solved order (CLS 0); this shuffles it in.
import { deal, gap, hint, isSolved, SIZES, slide, tileFor, type Dir, type Size, type SlidingState } from "./core"
import { randomSeed } from "../lib/rng"
import { readJSON, writeJSON } from "../lib/store"
import { formatLong, isToday, mark } from "../lib/daily"
import { gameEvent } from "../lib/game-events"
import { gameProgress, newAttempt } from "../lib/stats"

export interface SlidingDaily { game: string; date: string; seed: string; image: string }
export interface Picture { slug: string; name: string; width: number; height: number; credit: string }
interface Saved { v: 1; state: SlidingState; past: SlidingState[] }
interface Pref { size?: number; image?: string }
const PREF = "dt:pref:sliding-puzzle"
const KEYS: Record<string, Dir> = { ArrowLeft: "left", ArrowRight: "right", ArrowUp: "up", ArrowDown: "down" }

export class SlidingGame {
  private state!: SlidingState
  private past: SlidingState[] = []
  private undos = 0
  private hints = 0
  private hinted = -1
  private lastSlid = -1
  private sentFirst = false
  private readonly pictures: Picture[]
  private readonly status = document.querySelector<HTMLElement>("[data-status]")
  private readonly toolbar = document.querySelector<HTMLElement>("[data-toolbar]")
  private readonly sizes = document.querySelector<HTMLElement>("[data-sp-sizes]")
  private readonly chooser = document.querySelector<HTMLElement>("[data-sp-pictures]")
  private readonly credit = document.querySelector<HTMLElement>("[data-sp-credit]")
  private readonly grid: HTMLElement

  constructor(private readonly el: HTMLElement, private readonly daily?: SlidingDaily) {
    this.grid = el.querySelector<HTMLElement>("[data-grid]")!
    this.pictures = JSON.parse(el.dataset.pictures ?? "[]") as Picture[]
    const pref = readJSON<Pref>(PREF) ?? {}
    const size = daily ? 4 : this.validSize(pref.size ?? Number(el.dataset.variant))
    const image = daily ? daily.image : this.validImage(pref.image ?? el.dataset.image ?? "numbers")
    const saved = readJSON<Saved>(this.saveKey(size))
    if (saved?.v === 1 && saved.state?.size === size && (!daily || saved.state.seed === daily.seed)) {
      this.state = saved.state
      this.past = saved.past ?? []
      this.say(isSolved(this.state) ? "Welcome back. You solved this one. Choose New puzzle for another." : "Welcome back. Your puzzle is where you left it.")
    } else {
      this.state = { ...deal(daily?.seed ?? new URLSearchParams(location.search).get("seed") ?? randomSeed(), size, image), attempt: newAttempt() }
    }
    this.wire()
    this.render()
  }

  private validSize(n: number): Size { return (SIZES as readonly number[]).includes(n) ? (n as Size) : 4 }
  private validImage(slug: string): string { return slug === "numbers" || this.pictures.some((p) => p.slug === slug) ? slug : "numbers" }
  private saveKey(size: number): string {
    return this.daily ? `dt:save:daily:${this.daily.game}:${this.daily.date}:v1` : `dt:save:sliding-puzzle-${size}:v1`
  }
  private get ruleset(): string { return `sliding-puzzle:${this.state.size}` }
  private picture(): Picture | null { return this.pictures.find((p) => p.slug === this.state.image) ?? null }

  /** Slide the tile at square i, if it's in line with the gap. */
  tap(i: number): void {
    const s = this.state
    if (isSolved(s)) return this.say("This puzzle is solved. Choose New puzzle for another, or Undo to play the end again.")
    const next = slide(s, i)
    if (next === s) return this.say(i === gap(s) ? "That's the empty square. Tap a tile next to it." : "That tile isn't in line with the empty square. Tap a tile in the same row or column as the gap.")
    this.past.push(s)
    if (this.past.length > 500) this.past.shift()
    this.state = next
    this.hinted = -1
    this.lastSlid = gap(s)
    this.persist()
    this.render()
    if (!this.sentFirst) { this.sentFirst = true; gameEvent({ type: "first_move", ruleset: this.ruleset, daily: !!this.daily }) }
    const won = isSolved(next)
    if (won) gameEvent({ type: "end", ruleset: this.ruleset, outcome: "win", moves: next.moves, undos: this.undos, hints: this.hints, deal: next.seed, daily: !!this.daily })
    if (this.daily) mark(this.daily.game, this.daily.date, won ? "won" : "played")
    if (won) return this.say(this.daily ? (isToday(this.daily.date) ? `You solved today's puzzle in ${next.moves} moves! Come back tomorrow for a new picture.` : `You solved the puzzle for ${formatLong(this.daily.date)} in ${next.moves} moves!`) : `Solved in ${next.moves} moves!`)
    const n = s.size
    const moved = next.moves - s.moves
    this.say(`${moved > 1 ? `${moved} tiles slid` : `Tile ${s.tiles[i]} slid`} ${this.direction(i, gap(s), n)}. The gap is at row ${Math.floor(gap(next) / n) + 1}, column ${(gap(next) % n) + 1}.`)
  }

  private direction(from: number, to: number, n: number): string {
    const fr = Math.floor(from / n), fc = from % n, tr = Math.floor(to / n), tc = to % n
    return fr === tr ? (tc < fc ? "left" : "right") : tr < fr ? "up" : "down"
  }

  undo(): void {
    const prev = this.past.pop()
    if (!prev) return this.say("Nothing to undo.")
    this.undos++
    this.state = prev
    this.hinted = -1; this.lastSlid = -1
    this.persist(); this.render(); this.say("Undone.")
  }

  /** The same shuffle again. Undoable. */
  restart(): void {
    const s = this.state
    if (s.moves === 0) return this.say("You haven't moved yet.")
    if (this.sentFirst && !isSolved(s)) gameEvent({ type: "end", ruleset: this.ruleset, outcome: "new_game", moves: s.moves, undos: this.undos, hints: this.hints })
    this.sentFirst = false; this.undos = 0; this.hints = 0
    this.past.push(s)
    this.state = { ...deal(s.seed, s.size, s.image), attempt: newAttempt() }
    this.hinted = -1; this.lastSlid = -1
    this.persist(); this.render()
    this.say("Started this puzzle again. Undo brings back your moves.")
  }

  newGame(size: Size = this.state.size as Size, image: string = this.state.image): void {
    if (this.sentFirst && !isSolved(this.state)) gameEvent({ type: "end", ruleset: this.ruleset, outcome: "new_game", moves: this.state.moves, undos: this.undos, hints: this.hints })
    this.sentFirst = false; this.undos = 0; this.hints = 0; this.hinted = -1; this.lastSlid = -1
    writeJSON(PREF, { size, image } satisfies Pref)
    if (size !== this.state.size) {
      const saved = readJSON<Saved>(this.saveKey(size))
      if (saved?.v === 1 && saved.state?.size === size && !isSolved(saved.state)) {
        this.state = saved.state; this.past = saved.past ?? []
        this.render()
        return this.say(`${size} by ${size}: your puzzle is where you left it.`)
      }
    }
    this.past = []
    this.state = { ...deal(randomSeed(), size, image), attempt: newAttempt() }
    this.persist(); this.render()
    const pic = this.picture()
    this.say(`New ${size} by ${size} puzzle${pic ? `: ${pic.name}` : " with numbers"}. Tap a tile next to the gap to slide it.`)
  }

  showHint(): void {
    this.hints++
    const i = hint(this.state, this.lastSlid)
    if (i < 0) return this.say("This puzzle is solved.")
    this.hinted = i
    this.render()
    const n = this.state.size
    this.say(`Hint: slide tile ${this.state.tiles[i]} (row ${Math.floor(i / n) + 1}, column ${(i % n) + 1}).`)
  }

  private wire(): void {
    this.grid.addEventListener("click", (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>("[data-i]")
      if (b) this.tap(Number(b.dataset.i))
    })
    this.grid.addEventListener("keydown", (e) => {
      const d = KEYS[e.key]
      if (d) {
        e.preventDefault()
        const i = tileFor(this.state, d)
        if (i < 0) return this.say(`No tile can slide ${d}: the gap is at the edge.`)
        this.tap(i)
      } else if (e.key === "h" || e.key === "H") this.showHint()
    })
    // Ctrl+Z anywhere on the page: the finish card takes focus after a win, and Undo should still work there.
    document.addEventListener("keydown", (e) => {
      const t = e.target as HTMLElement | null
      if (t?.matches("input, textarea, select")) return
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); this.undo() }
    })
    this.toolbar?.addEventListener("click", (e) => {
      const act = (e.target as HTMLElement).closest<HTMLElement>("[data-act]")?.dataset.act
      if (act === "undo") this.undo()
      else if (act === "hint") this.showHint()
      else if (act === "new") this.newGame()
      else if (act === "restart") this.restart()
    })
    this.sizes?.addEventListener("click", (e) => {
      const n = Number((e.target as HTMLElement).closest<HTMLElement>("[data-size]")?.dataset.size)
      if (n && n !== this.state.size) this.newGame(this.validSize(n))
    })
    this.chooser?.addEventListener("click", (e) => {
      const slug = (e.target as HTMLElement).closest<HTMLElement>("[data-image]")?.dataset.image
      if (slug && slug !== this.state.image) this.newGame(this.state.size as Size, this.validImage(slug))
    })
  }

  private say(msg: string): void { if (this.status) this.status.textContent = msg }

  private persist(): void {
    writeJSON(this.saveKey(this.state.size), { v: 1, state: this.state, past: this.past.slice(-60) } satisfies Saved)
  }

  render(): void {
    const s = this.state
    gameProgress({ key: this.saveKey(s.size), ruleset: this.ruleset, id: `${s.size}:${s.seed}:${s.image}#${s.attempt ?? 0}`, moves: s.moves, won: isSolved(s) })
    const n = s.size
    const pic = this.picture()
    this.el.dataset.variant = String(n)
    this.el.dataset.image = s.image
    this.el.classList.toggle("is-numbers", !pic)
    this.el.classList.toggle("is-portrait", !!pic && pic.height > pic.width)
    this.el.classList.toggle("is-solved", isSolved(s))
    this.el.style.setProperty("--a", pic ? String(pic.width / pic.height) : "1")
    this.el.style.setProperty("--img", pic ? `url("/jigsaw/${pic.slug}.webp")` : "none")
    this.sizes?.querySelectorAll<HTMLElement>("[data-size]").forEach((b) => b.setAttribute("aria-pressed", String(Number(b.dataset.size) === n)))
    this.chooser?.querySelectorAll<HTMLElement>("[data-image]").forEach((b) => b.setAttribute("aria-pressed", String(b.dataset.image === s.image)))
    if (this.credit) this.credit.textContent = pic ? `Picture: ${pic.credit}` : ""
    if (this.grid.children.length !== n * n) {
      this.grid.style.setProperty("--n", String(n))
      this.grid.innerHTML = Array.from({ length: n * n }, (_, i) => `<button type="button" class="sp-tile" data-i="${i}" tabindex="-1"></button>`).join("")
    }
    this.grid.setAttribute("aria-label", `Sliding puzzle, ${n} by ${n}${pic ? `, ${pic.name}` : ", numbers"}. Arrow keys slide a tile into the gap.`)
    const g = gap(s)
    const solvedNow = isSolved(s)
    this.grid.querySelectorAll<HTMLButtonElement>("[data-i]").forEach((b, i) => {
      const v = s.tiles[i]!
      const r = Math.floor(i / n), c = i % n
      const home = v ? v - 1 : -1
      b.className = `sp-tile${v ? "" : " is-gap"}${v && home === i ? " is-home" : ""}${i === this.hinted ? " is-hint" : ""}`
      // The picture region the tile shows is its home square, not where it sits now.
      b.style.setProperty("--r", String(v ? Math.floor(home / n) : 0))
      b.style.setProperty("--c", String(v ? home % n : 0))
      b.textContent = ""
      if (v) {
        const num = document.createElement("span")
        num.className = "sp-num"
        num.textContent = String(v)
        b.append(num)
      }
      b.hidden = !v && !solvedNow
      const inLine = v && (r === Math.floor(g / n) || c === g % n)
      b.setAttribute("aria-label", v ? `Tile ${v}, row ${r + 1}, column ${c + 1}${inLine ? ", can slide" : ""}` : `Empty square, row ${r + 1}, column ${c + 1}`)
      // Every tile stays a live button: a tap on one out of line gets an explanation, not a dead button.
      b.disabled = !v || solvedNow
    })
  }
}
